import React, { useState, useEffect } from 'react';
import { api } from '../api';

function Admin({ user }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({ name: '', admin_email: '', admin_password: '', sip_username: '', sip_password: '', sip_server: '89.117.144.204' });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const data = await api.getTenants();
      setTenants(data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);

    try {
      await api.createTenant(formData);
      setShowCreateModal(false);
      setFormData({ name: '', admin_email: '', admin_password: '', sip_username: '', sip_password: '', sip_server: '89.117.144.204' });
      loadTenants();
    } catch (error) {
      setError(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      admin_email: tenant.admin_email,
      admin_password: '', // Leave empty - optional update
      sip_username: tenant.sip_username,
      sip_password: tenant.sip_password,
      sip_server: tenant.sip_server
    });
    setShowEditModal(true);
    setError('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setUpdating(true);

    try {
      await api.updateTenant(editingTenant.id, formData);
      setShowEditModal(false);
      setEditingTenant(null);
      setFormData({ name: '', admin_email: '', admin_password: '', sip_username: '', sip_password: '', sip_server: '89.117.144.204' });
      loadTenants();
    } catch (error) {
      setError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Are you sure you want to delete tenant "${tenant.name}"? This will delete all campaigns, leads, and calls.`)) {
      return;
    }

    try {
      await api.deleteTenant(tenant.id);
      loadTenants();
    } catch (error) {
      alert('Failed to delete tenant: ' + error.message);
    }
  };

  if (user.role !== 'super_admin') {
    return (
      <div className="card">
        <p style={{ color: 'hsl(var(--red))' }}>Access denied. Super admin only.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 600 }}>Tenant Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
          data-testid="btn-create-tenant"
        >
          + Create Tenant
        </button>
      </div>

      <div className="card">
        {tenants.length === 0 ? (
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>No tenants yet. Create your first tenant!</p>
        ) : (
          <div className="table-container">
            <table data-testid="tenants-table">
              <thead>
                <tr>
                  <th>Tenant Name</th>
                  <th>Admin Email</th>
                  <th>SIP Username</th>
                  <th>SIP Server</th>
                  <th>Campaigns</th>
                  <th>Total Calls</th>
                  <th>Total Minutes</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(tenant => (
                  <tr key={tenant.id}>
                    <td>{tenant.name}</td>
                    <td>{tenant.admin_email}</td>
                    <td style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>{tenant.sip_username}</td>
                    <td style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>{tenant.sip_server}</td>
                    <td>{tenant.total_campaigns}</td>
                    <td>{tenant.total_calls}</td>
                    <td>{tenant.total_minutes.toFixed(1)}</td>
                    <td style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                          onClick={() => handleEdit(tenant)}
                          data-testid={`btn-edit-tenant-${tenant.id}`}
                        >
                          Edit
                        </button>
                        <button
                          className="btn"
                          style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: 'hsl(var(--red))', color: 'white' }}
                          onClick={() => handleDelete(tenant)}
                          data-testid={`btn-delete-tenant-${tenant.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Create New Tenant</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Tenant Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-tenant-name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  required
                  data-testid="input-admin-email"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  required
                  data-testid="input-admin-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label">SIP Username</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="From Magnus Billing"
                  value={formData.sip_username}
                  onChange={(e) => setFormData({ ...formData, sip_username: e.target.value })}
                  required
                  data-testid="input-sip-username"
                />
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                  SIP username from Magnus Billing (worldoftelco.me)
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">SIP Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="From Magnus Billing"
                  value={formData.sip_password}
                  onChange={(e) => setFormData({ ...formData, sip_password: e.target.value })}
                  required
                  data-testid="input-sip-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label">SIP Server</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., 89.117.144.204 or worldoftelco.me"
                  value={formData.sip_server}
                  onChange={(e) => setFormData({ ...formData, sip_server: e.target.value })}
                  required
                  data-testid="input-sip-server"
                />
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                  SIP server IP address or domain name
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                  data-testid="btn-submit-tenant"
                >
                  {creating ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit Tenant</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ×
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label className="form-label">Tenant Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-edit-tenant-name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  required
                  data-testid="input-edit-admin-email"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Admin Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Leave empty to keep current password"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  data-testid="input-edit-admin-password"
                />
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                  Leave empty to keep current password
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">SIP Username</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="From Magnus Billing"
                  value={formData.sip_username}
                  onChange={(e) => setFormData({ ...formData, sip_username: e.target.value })}
                  required
                  data-testid="input-edit-sip-username"
                />
              </div>

              <div className="form-group">
                <label className="form-label">SIP Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="From Magnus Billing"
                  value={formData.sip_password}
                  onChange={(e) => setFormData({ ...formData, sip_password: e.target.value })}
                  required
                  data-testid="input-edit-sip-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label">SIP Server</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., 89.117.144.204 or worldoftelco.me"
                  value={formData.sip_server}
                  onChange={(e) => setFormData({ ...formData, sip_server: e.target.value })}
                  required
                  data-testid="input-edit-sip-server"
                />
                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                  SIP server IP address or domain name
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                  data-testid="btn-submit-edit-tenant"
                >
                  {updating ? 'Updating...' : 'Update Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;